import { AlertFacilitiesHelper } from './alert-facilities-helper';
import { AlertModelService } from '../alert-model.service';
import { AlertFields } from '..';
import { deepCopy } from 'src/app/shared/model/utility';
import { LogHelper } from 'src/app/shared/logging/log-helper';
import { AccessFilterCondition, AccessRecipe } from 'src/app/hickory-api';
import { AccessRecipeHelper } from 'src/app/shared/model/accessrecipe-helper';
import { Directory } from 'src/app/shared/directory/base/models';

export class AlertRecipientsSearchingHelper {
  private static readonly variableResolvers = {
    facilityIds: (alertModel: AlertModelService) =>
      AlertFacilitiesHelper.selectedFacilities(alertModel).map((f) => f.id) ?? [],
  };

  static isVariable(value: string): boolean {
    return value?.trim()?.startsWith('{{') && value?.trim()?.endsWith('}}');
  }

  static getVariableName(value: string): string {
    if (!this.isVariable(value)) return null;

    return value?.trim()?.slice(2, -2).trim();
  }

  static getVariableAlertField(value: string): string[] {
    if (!this.isVariable(value)) return null;

    const variableName = this.getVariableName(value);
    if (!variableName) return null;

    // eslint-disable-next-line sonarjs/no-small-switch
    switch (variableName) {
      case 'facilityIds':
        return [AlertFields.FACILITIES];
      default:
        return null;
    }
  }

  static resolveFilters(
    alertModel: AlertModelService,
    filters: AccessRecipe[] = alertModel?.alertRecipients?.searchFilters,
    directoryId: string = Directory.HK_INTERNAL_DIRECTORY_ID,
    splitDirectory: boolean = false,
  ): AccessRecipe[] {
    return (
      filters
        ?.map((f) => AlertRecipientsSearchingHelper.resolveFilter(f, alertModel))
        ?.flatMap((f) => AlertRecipientsSearchingHelper.withClassificationFilter(f, directoryId, splitDirectory)) ?? []
    );
  }

  static resolveFilter(recipe: AccessRecipe, alertModel: AlertModelService): AccessRecipe {
    if (!AccessRecipeHelper.isValid(recipe)) return recipe;

    try {
      const filter: AccessRecipe = deepCopy(recipe);
      filter.filter.conditions = filter.filter.conditions.map((c) => this.substituteVariable({ ...c }, alertModel));
      return filter;
    } catch (error) {
      LogHelper.debug('AlertRecipientsSearchingHelper', 'Error resolving filter', error);
      return recipe;
    }
  }

  static isValidForDirectory(recipe: AccessRecipe, directoryId: string, splitDirectory: boolean): boolean {
    if (!splitDirectory) {
      return true;
    }

    return recipe.type === directoryId;
  }

  static withClassificationFilter(
    recipe: AccessRecipe,
    directoryId: string,
    splitDirectory: boolean = false,
  ): AccessRecipe {
    if (!splitDirectory || !AccessRecipeHelper.isValid(recipe)) {
      return recipe;
    }

    const hasClassificationFilter = recipe.filter?.conditions?.some(
      condition => condition.field === 'classification'
    );

    if (hasClassificationFilter) {
      return recipe;
    }

    if (directoryId === Directory.HK_PUBLIC_DIRECTORY_ID) {
      const publicRecipe = deepCopy(recipe);
      publicRecipe.filter.conditions.push(AccessRecipeHelper.createCondition('classification', 'oneOf', ['public']));
      return publicRecipe;
    }

    const internalRecipe = deepCopy(recipe);
    internalRecipe.filter.conditions.push(AccessRecipeHelper.createCondition('classification', 'oneOf', ['internal']));
    return internalRecipe;
  }

  private static substituteVariable(
    condition: AccessFilterCondition,
    alertModel: AlertModelService,
  ): AccessFilterCondition {
    if (!AccessRecipeHelper.isValidFilterCondition(condition)) return condition;

    const variableValues = condition.values.filter((v) => this.isVariable(v));
    if (!variableValues.length) return condition;

    const nonVariables = condition.values.filter((v) => !this.isVariable(v));
    const resolvedValues: string[] = [];
    for (const variableValue of variableValues) {
      const variableName = this.getVariableName(variableValue);
      if (!variableName) continue;

      const resolver: (alertModel: AlertModelService) => string[] = this.variableResolvers[variableName];
      if (!resolver) continue;

      const resolved = resolver(alertModel);
      if (!resolved.length) continue;

      resolvedValues.push(...resolved);
    }

    return {
      ...condition,
      values: [...new Set([...nonVariables, ...resolvedValues])],
    };
  }
}
